
import React, { useState, useMemo } from 'react';
import { TransactionStatus } from '../types';
import { DollarSign, ArrowRight, Wallet as WalletIcon, Zap, ArrowLeftRight, Info, TrendingUp, Users, ShieldCheck, Star } from 'lucide-react';
import { useSignalData } from '../context/SignalContext';

export const WalletView: React.FC = () => {
    const { stats, currentUser, transactions, users, transferIncomeToReward } = useSignalData();
    const [transferAmount, setTransferAmount] = useState(0);

    // Tính toán chi tiết thu nhập từ lịch sử giao dịch
    const incomeDetails = useMemo(() => {
        if (!currentUser) return { t_direct: 0, k_system: 0, group_bonus: 0 };

        // Thu nhập trực tiếp (T) - Tiền vào Ví Thu Nhập
        const t_direct = transactions
            .filter(t => t.affiliateId === currentUser.id && t.status === TransactionStatus.APPROVED)
            .reduce((sum, t) => sum + (t.commission || 0), 0);

        // Thu nhập hệ thống (K) - Tiền vào Ví Thưởng
        const myF1s = users.filter(u => u.referredBy === currentUser.tradingViewId || u.referredBy === currentUser.id);
        const k_system = transactions
            .filter(t => t.status === TransactionStatus.APPROVED && myF1s.some(f1 => f1.id === t.affiliateId))
            .reduce((sum, t) => sum + (t.indirectCommission || 0), 0);

        // Thu nhập nhóm (X1 + Top Rank)
        const group_and_other = Math.max(0, stats.incomeBalance - t_direct);

        return {
            t_direct,
            k_system,
            group_bonus: group_and_other
        };
    }, [currentUser, transactions, users, stats.incomeBalance]);

    const handleTransfer = async () => {
        if(transferAmount <= 0) return;
        if(confirm(`Bạn có chắc muốn chuyển $${transferAmount} từ Ví Thu Nhập sang Ví Thưởng? Hành động này không thể hoàn tác.`)) {
            await transferIncomeToReward(transferAmount);
            setTransferAmount(0);
            alert("Chuyển đổi thành công! Số dư đã được cập nhật.");
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <h2 className="text-xl font-black text-slate-800 flex items-center gap-2 uppercase tracking-wide">
                    <WalletIcon className="w-6 h-6 text-brand-600" />
                    Quản lý Thu nhập
                </h2>
                <p className="text-slate-400 text-sm mt-1">Theo dõi các nguồn doanh thu từ hoạt động Affiliate và Đội nhóm.</p>
            </div>

            {/* WALLET BALANCES */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white p-8 rounded-[32px] shadow-xl relative overflow-hidden">
                    <div className="relative z-10">
                        <p className="text-emerald-100 text-[10px] font-black mb-2 flex items-center gap-2 uppercase tracking-[0.2em]">
                            <DollarSign className="w-4 h-4"/> VÍ THU NHẬP (Income)
                        </p>
                        <h2 className="text-6xl font-black tracking-tighter">${stats.incomeBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</h2>
                        <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-2">
                             <TrendingUp className="w-4 h-4 text-emerald-300" />
                             <p className="text-xs text-emerald-100/80 font-medium">Nguồn: Hoa hồng trực tiếp (T) + Thưởng Nhóm</p>
                        </div>
                    </div>
                    <DollarSign className="absolute right-[-20px] bottom-[-20px] w-48 h-48 text-emerald-500 opacity-10 rotate-12" />
                </div>
                
                <div className="bg-gradient-to-br from-orange-500 to-orange-700 text-white p-8 rounded-[32px] shadow-xl relative overflow-hidden">
                    <div className="relative z-10">
                        <p className="text-orange-100 text-[10px] font-black mb-2 flex items-center gap-2 uppercase tracking-[0.2em]">
                            <Zap className="w-4 h-4"/> VÍ THƯỞNG (Reward)
                        </p>
                        <h2 className="text-6xl font-black tracking-tighter">${stats.rewardBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</h2>
                        <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-2">
                             <Users className="w-4 h-4 text-orange-300" />
                             <p className="text-xs text-orange-100/80 font-medium">Nguồn: Hoa hồng hệ thống (K)</p>
                        </div>
                    </div>
                    <Zap className="absolute right-[-20px] bottom-[-20px] w-48 h-48 text-orange-400 opacity-10 rotate-12" />
                </div>
            </div>

            {/* DETAILED BREAKDOWN */}
            <div className="bg-white rounded-[32px] shadow-sm border border-slate-100 p-8">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                        <Info className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Chi tiết nguồn thu nhập</h3>
                        <p className="text-xs text-slate-400 font-medium">Diễn giải các khoản thu nhập đã nhận được</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Source T */}
                    <div className="p-6 rounded-3xl bg-slate-50 border border-slate-100 flex flex-col h-full hover:border-emerald-200 transition-colors group">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            <TrendingUp className="w-5 h-5" />
                        </div>
                        <h4 className="font-black text-slate-800 text-sm uppercase tracking-wider mb-1">Trực tiếp (T)</h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mb-4">Giới thiệu trực tiếp</p>
                        <div className="mt-auto">
                            <p className="text-3xl font-black text-emerald-600 tracking-tight">${incomeDetails.t_direct.toLocaleString()}</p>
                            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed font-medium">Nhận ngay khi thành viên F1 của bạn kích hoạt gói SignalViet thành công.</p>
                        </div>
                    </div>

                    {/* Source K */}
                    <div className="p-6 rounded-3xl bg-slate-50 border border-slate-100 flex flex-col h-full hover:border-orange-200 transition-colors group">
                        <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            <Users className="w-5 h-5" />
                        </div>
                        <h4 className="font-black text-slate-800 text-sm uppercase tracking-wider mb-1">Hệ thống (K)</h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mb-4">Giới thiệu gián tiếp</p>
                        <div className="mt-auto">
                            <p className="text-3xl font-black text-orange-600 tracking-tight">${incomeDetails.k_system.toLocaleString()}</p>
                            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed font-medium">Thu nhập thụ động từ doanh số của các thành viên cấp dưới trong hệ thống.</p>
                        </div>
                    </div>

                    {/* Source Group */}
                    <div className="p-6 rounded-3xl bg-slate-50 border border-slate-100 flex flex-col h-full hover:border-blue-200 transition-colors group relative overflow-hidden">
                        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            <Star className="w-5 h-5" />
                        </div>
                        <h4 className="font-black text-slate-800 text-sm uppercase tracking-wider mb-1">Thưởng Nhóm</h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mb-4">X1 Fund & Top Rank</p>
                        <div className="mt-auto">
                            <p className="text-3xl font-black text-blue-600 tracking-tight">${incomeDetails.group_bonus.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed font-medium">Bao gồm <b>Quỹ Nhóm X1</b>, thưởng <b>Top 1-2-3</b> và các khoản cộng thêm từ Admin.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* TRANSFER WIDGET */}
            <div className="bg-white rounded-[32px] shadow-sm border border-slate-100 overflow-hidden">
                <div className="flex border-b border-slate-50 bg-slate-50/50">
                    <div className="px-10 py-5 text-xs font-black text-brand-600 border-b-2 border-brand-600 flex items-center gap-3 uppercase tracking-[0.2em]">
                        <ArrowLeftRight className="w-4 h-4"/> Chuyển Ví
                    </div>
                </div>

                <div className="p-10">
                    <div className="max-w-2xl mx-auto space-y-10">
                        <div className="flex items-center justify-center gap-8">
                            <div className="flex-1 bg-emerald-50/50 border border-emerald-100 p-6 rounded-3xl text-center shadow-sm">
                                <p className="text-[9px] font-black text-emerald-600 uppercase tracking-[0.15em] mb-2">Ví Thu Nhập</p>
                                <p className="text-2xl font-black text-slate-800">${stats.incomeBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                            </div>
                            <div className="bg-slate-100 p-3 rounded-full border border-slate-200">
                                <ArrowRight className="text-slate-400 w-6 h-6" />
                            </div>
                            <div className="flex-1 bg-orange-50/50 border border-orange-100 p-6 rounded-3xl text-center shadow-sm">
                                <p className="text-[9px] font-black text-orange-600 uppercase tracking-[0.15em] mb-2">Ví Thưởng</p>
                                <p className="text-2xl font-black text-slate-800">${stats.rewardBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-4 ml-1">Số tiền muốn chuyển ($)</label>
                                <input 
                                    type="number" 
                                    min="0"
                                    max={stats.incomeBalance}
                                    value={transferAmount}
                                    onChange={e => setTransferAmount(Number(e.target.value))}
                                    className="w-full p-6 bg-white border border-slate-200 rounded-2xl font-black text-4xl text-slate-800 focus:ring-8 focus:ring-brand-500/5 focus:border-brand-500 outline-none transition-all placeholder:text-slate-200"
                                    placeholder="0"
                                />
                                <div className="flex items-start gap-3 mt-6 text-xs text-slate-400 font-medium italic leading-relaxed">
                                    <ShieldCheck className="w-5 h-5 shrink-0 text-slate-300" />
                                    <p>Tiền đã chuyển sang Ví Thưởng <b>KHÔNG THỂ</b> chuyển ngược lại Ví Thu Nhập. Ví Thưởng chỉ dùng để thanh toán phí duy trì/gia hạn tài khoản.</p>
                                </div>
                            </div>

                            <button 
                                onClick={handleTransfer}
                                disabled={transferAmount <= 0 || transferAmount > stats.incomeBalance}
                                className="w-full bg-slate-900 text-white py-6 rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-brand-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all shadow-xl shadow-slate-200 active:scale-95"
                            >
                                Xác Nhận Chuyển Đổi
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
